import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

const TrustAccountDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [isEditing, setIsEditing] = useState(false);
    const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    const getAuthHeaders = () => {
        const token = localStorage.getItem('accessToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    };

    const fetchAccount = useCallback(async () => {
        try {
            const response = await fetch(`/api/trust/accounts/${id}/`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch account');
            const data = await response.json();
            setAccount(data);
            setEditData({
                account_name: data.account_name,
                bank_name: data.bank_name,
                bank_branch: data.bank_branch || '',
                account_type: data.account_type,
                currency: data.currency,
                requires_dual_signature: data.requires_dual_signature,
                notes: data.notes || ''
            });
        } catch (err) {
            showToast(err.message, 'error');
        }
    }, [id]);

    const fetchTransactions = useCallback(async () => {
        try {
            const response = await fetch(`/api/trust/accounts/${id}/transactions/`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch transactions');
            const data = await response.json();
            setTransactions(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching transactions:', err);
        }
    }, [id]);

    const fetchLedgers = useCallback(async () => {
        try {
            const response = await fetch(`/api/trust/accounts/${id}/client_ledgers/`, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to fetch ledgers');
            const data = await response.json();
            setLedgers(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching ledgers:', err);
        }
    }, [id]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchAccount(), fetchTransactions(), fetchLedgers()]);
            setLoading(false);
        };
        loadData();
    }, [fetchAccount, fetchTransactions, fetchLedgers]);

    const handleSaveEdit = async () => {
        setSaving(true);
        try {
            const response = await fetch(`/api/trust/accounts/${id}/`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(editData)
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.detail || 'Failed to update account');
            }
            await fetchAccount();
            setIsEditing(false);
            showToast('Account updated successfully');
        } catch (err) {
            showToast(err.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivate = async () => {
        try {
            const response = await fetch(`/api/trust/accounts/${id}/`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to deactivate account');
            }
            showToast('Account deactivated successfully');
            setTimeout(() => navigate('/trust/accounts'), 1000);
        } catch (err) {
            showToast(err.message, 'error');
            setShowDeactivateDialog(false);
        }
    };

    const handleRecalculate = async () => {
        try {
            const response = await fetch(`/api/trust/accounts/${id}/recalculate_balance/`, {
                method: 'POST',
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error('Failed to recalculate');
            await fetchAccount();
            showToast('Balance recalculated successfully');
        } catch (err) {
            showToast(err.message, 'error');
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount || 0);
    };

    const accountTypes = {
        IOLTA: 'Interest on Lawyers Trust Account',
        TRUST: 'Client Trust Account',
        ESCROW: 'Escrow Account',
        RETAINER: 'Retainer Account'
    };

    const accountTypeOptions = [
        { value: 'IOLTA', label: 'Interest on Lawyers Trust Account (IOLTA)' },
        { value: 'TRUST', label: 'Client Trust Account' },
        { value: 'ESCROW', label: 'Escrow Account' },
        { value: 'RETAINER', label: 'Retainer Account' }
    ];

    const currencyOptions = [
        { value: 'USD', label: 'US Dollar (USD)' },
        { value: 'ZWL', label: 'Zimbabwe Dollar (ZWL)' },
        { value: 'ZAR', label: 'South African Rand (ZAR)' },
        { value: 'GBP', label: 'British Pound (GBP)' },
        { value: 'EUR', label: 'Euro (EUR)' }
    ];

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="h-4 w-48 bg-gray-200 rounded animate-pulse mb-4"></div>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2"></div>
                        <div className="flex gap-2">
                            <div className="h-5 w-16 bg-gray-100 rounded-full animate-pulse"></div>
                            <div className="h-5 w-24 bg-gray-100 rounded-full animate-pulse"></div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <div className="h-10 w-28 bg-gray-200 rounded-lg animate-pulse"></div>
                        <div className="h-10 w-36 bg-gray-200 rounded-lg animate-pulse"></div>
                    </div>
                </div>
                <div className="grid grid-cols-4 gap-4 mb-6">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                            <div className="h-3 w-20 bg-gray-100 rounded animate-pulse mb-3"></div>
                            <div className="h-7 w-24 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!account) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-md mx-auto mt-16">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
                        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Account Not Found</h2>
                        <p className="text-gray-500 mb-6">This trust account may have been removed or you don't have access.</p>
                        <Link
                            to="/trust/accounts"
                            className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            ← Back to Accounts
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 p-4 rounded-lg border shadow-lg max-w-sm transition-all ${toast.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                    {toast.type === 'error' ? (
                        <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    ) : (
                        <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    )}
                    <p className="text-sm text-gray-700">{toast.message}</p>
                </div>
            )}

            {/* Breadcrumb & Header */}
            <div className="mb-6">
                <nav className="text-sm text-gray-500 mb-2">
                    <Link to="/trust" className="hover:text-blue-600">Trust Accounts</Link>
                    <span className="mx-2">›</span>
                    <Link to="/trust/accounts" className="hover:text-blue-600">All Accounts</Link>
                    <span className="mx-2">›</span>
                    <span className="text-gray-900">{account.account_name}</span>
                </nav>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{account.account_name}</h1>
                        <div className="flex items-center gap-3 mt-1">
                            <span className="text-gray-500 font-mono">{account.account_number}</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${account.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {account.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                                {accountTypes[account.account_type] || account.account_type}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isEditing && (
                            <>
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                    Edit Account
                                </button>
                                <button
                                    onClick={() => navigate(`/trust/transactions/new?account=${id}`)}
                                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                                >
                                    Record Transaction
                                </button>
                                {account.is_active && (
                                    <button
                                        onClick={() => setShowDeactivateDialog(true)}
                                        className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50"
                                    >
                                        Deactivate
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Balance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <p className="text-sm text-gray-500 mb-1">Current Balance</p>
                    <p className={`text-2xl font-bold ${(account.current_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(account.current_balance, account.currency)}
                    </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <p className="text-sm text-gray-500 mb-1">Opening Balance</p>
                    <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(account.opening_balance, account.currency)}
                    </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <p className="text-sm text-gray-500 mb-1">Total Transactions</p>
                    <p className="text-2xl font-bold text-gray-900">{account.transaction_count || 0}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <p className="text-sm text-gray-500 mb-1">Active Clients</p>
                    <p className="text-2xl font-bold text-gray-900">{ledgers.filter(l => parseFloat(l.balance) !== 0).length}</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-200">
                    <nav className="flex -mb-px">
                        {[
                            { id: 'overview', label: 'Overview' },
                            { id: 'transactions', label: 'Transactions' },
                            { id: 'ledgers', label: 'Client Ledgers' },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="p-6">
                        {isEditing ? (
                            <div className="space-y-4 max-w-2xl">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Account Details</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                                        <input
                                            type="text"
                                            value={editData.account_name}
                                            onChange={(e) => setEditData({ ...editData, account_name: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                                        <input
                                            type="text"
                                            value={editData.bank_name}
                                            onChange={(e) => setEditData({ ...editData, bank_name: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                                        <input
                                            type="text"
                                            value={editData.bank_branch}
                                            onChange={(e) => setEditData({ ...editData, bank_branch: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                                        <select
                                            value={editData.account_type}
                                            onChange={(e) => setEditData({ ...editData, account_type: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            {accountTypeOptions.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                        <select
                                            value={editData.currency}
                                            onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            {currencyOptions.map(c => (
                                                <option key={c.value} value={c.value}>{c.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={editData.requires_dual_signature}
                                                onChange={(e) => setEditData({ ...editData, requires_dual_signature: e.target.checked })}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">Requires dual signature for withdrawals</span>
                                        </label>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                        <textarea
                                            value={editData.notes}
                                            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            rows="3"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-3 pt-4">
                                    <button
                                        onClick={handleSaveEdit}
                                        disabled={saving}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                                        Save Changes
                                    </button>
                                    <button
                                        onClick={() => setIsEditing(false)}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
                                    <dl className="space-y-3">
                                        {[
                                            ['Account Name', account.account_name],
                                            ['Account Number', <span className="font-mono">{account.account_number}</span>],
                                            ['Bank', account.bank_name],
                                            ['Branch', account.bank_branch || '—'],
                                            ['Type', accountTypes[account.account_type] || account.account_type],
                                            ['Currency', account.currency],
                                            ['Dual Signature', account.requires_dual_signature ? 'Required' : 'Not Required'],
                                        ].map(([label, value], i) => (
                                            <div key={i} className="flex justify-between py-2 border-b border-gray-100">
                                                <dt className="text-sm text-gray-500">{label}</dt>
                                                <dd className="text-sm font-medium text-gray-900">{value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Additional Details</h3>
                                    <dl className="space-y-3">
                                        {[
                                            ['Status', account.is_active ? 'Active' : 'Inactive'],
                                            ['Created', new Date(account.created_at).toLocaleDateString()],
                                            ['Last Updated', new Date(account.updated_at).toLocaleDateString()],
                                            ['Created By', account.created_by_name || '—'],
                                        ].map(([label, value], i) => (
                                            <div key={i} className="flex justify-between py-2 border-b border-gray-100">
                                                <dt className="text-sm text-gray-500">{label}</dt>
                                                <dd className="text-sm font-medium text-gray-900">{value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                    {account.notes && (
                                        <div className="mt-4">
                                            <h4 className="text-sm font-medium text-gray-500 mb-1">Notes</h4>
                                            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{account.notes}</p>
                                        </div>
                                    )}
                                    <button
                                        onClick={handleRecalculate}
                                        className="mt-4 px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
                                    >
                                        Recalculate Balance
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {transactions.length > 0 ? transactions.map((txn) => (
                                    <tr key={txn.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{txn.transaction_date}</td>
                                        <td className="px-6 py-4 text-sm font-mono text-gray-600 whitespace-nowrap">{txn.reference_number}</td>
                                        <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{txn.client_name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${txn.is_credit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {txn.transaction_type_display || txn.transaction_type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{txn.description}</td>
                                        <td className={`px-6 py-4 text-sm text-right font-medium whitespace-nowrap ${txn.is_credit ? 'text-green-600' : 'text-red-600'}`}>
                                            {txn.is_credit ? '+' : '-'}{formatCurrency(txn.amount, account.currency)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-right text-gray-900 whitespace-nowrap">
                                            {formatCurrency(txn.running_balance, account.currency)}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            No transactions found for this account
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Client Ledgers Tab */}
                {activeTab === 'ledgers' && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Transaction</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Updated</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {ledgers.length > 0 ? ledgers.map((ledger) => (
                                    <tr key={ledger.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{ledger.client_name}</td>
                                        <td className={`px-6 py-4 text-sm text-right font-semibold ${parseFloat(ledger.balance) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {formatCurrency(ledger.balance, account.currency)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{ledger.last_transaction_date || '—'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {ledger.last_updated ? new Date(ledger.last_updated).toLocaleDateString() : '—'}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                                            No client ledgers found for this account
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Deactivate Confirmation Dialog */}
            {showDeactivateDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDeactivateDialog(false)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 p-2 bg-red-100 rounded-full">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Deactivate Trust Account</h3>
                                <p className="mt-2 text-sm text-gray-600">
                                    Are you sure you want to deactivate <strong>{account.account_name}</strong>?
                                    This action can only proceed if the account has a zero balance.
                                    The account will be marked as inactive but not deleted.
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => setShowDeactivateDialog(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeactivate}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                            >
                                Deactivate Account
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrustAccountDetail;
